import React, { useEffect, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  IconButton, 
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Zoom,
  CssBaseline
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Person as ProfileIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  FactCheck as FactCheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

// Função helper para verificar o papel do usuário diretamente no localStorage
const getUserRole = () => {
  const role = localStorage.getItem('role');
  return role ? parseInt(role) : null;
};

// Função helper para verificar se é admin
const isAdmin = () => {
  return getUserRole() === 1;
};

// Função helper para verificar se é técnico
const isTechnical = () => {
  return getUserRole() === 3;
};

// Largura da drawer
const drawerWidth = 240;
const drawerCollapsedWidth = 64;

// Componente Main estilizado
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(2),
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: open ? 0 : drawerCollapsedWidth,
    width: `calc(100% - ${open ? 0 : drawerCollapsedWidth}px)`,
    overflow: 'auto',
    height: '100vh',
    paddingTop: '64px',
  }),
);

// Header da Drawer
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  minHeight: '64px',
  justifyContent: 'space-between',
}));

const Navbar = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { toggleColorMode, mode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [roleText, setRoleText] = useState('Usuário');

  useEffect(() => {
    fetchUserData();
    
    // Ajustar estado do drawer para dispositivos móveis
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
        
        const response = await api.get('/users/me');
        console.log('Dados do usuário:', response.data);
        
        if (response.data) {
        // Guardar role_id no localStorage
        localStorage.setItem('role', response.data.role_id.toString());
          
        // Atualizar nome de usuário e avatar
          setUsername(response.data.username || '');
          
        // Usar a URL da imagem de perfil se disponível, caso contrário usar o gerador de avatar
        if (response.data.profile_image_url) {
          setAvatarUrl(response.data.profile_image_url);
        } else {
          setAvatarUrl(`https://ui-avatars.com/api/?name=${response.data.username}&background=random`);
        }
        
        // Definir texto do papel baseado na role_id
        if (response.data.role_id === 1) {
          setRoleText('Administrador');
        } else if (response.data.role_id === 3) {
          setRoleText('Técnico');
        } else {
          setRoleText('Usuário');
        }
        }
      } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleProfileClick = () => {
    console.log('Navegando para a página de perfil...');
    navigate('/profile');
  };

  // Função para construir os itens do menu baseado no papel do usuário
  const getNavItems = () => {
    // Sempre incluir estas opções
  const navItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Projetos', icon: <FactCheckIcon />, path: '/projects' },
  ];
  
    const role = getUserRole();
    
    // Admin (role_id = 1)
    if (role === 1) {
      navItems.push(
        { text: 'Responsáveis', icon: <PersonIcon />, path: '/responsibles' },
        { text: 'Análise de Viabilidade', icon: <AssessmentIcon />, path: '/feasibility' },
        { text: 'Usuários', icon: <PeopleIcon />, path: '/users' }
      );
    }
    // Técnico (role_id = 3)
    else if (role === 3) {
      navItems.push(
        { text: 'Responsáveis', icon: <PersonIcon />, path: '/responsibles' },
        { text: 'Análise de Viabilidade', icon: <AssessmentIcon />, path: '/feasibility' },
        { text: 'Usuários', icon: <PeopleIcon />, path: '/users' }
      );
    }
    
    return navItems;
  };

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          width: '100%',
          boxShadow: 3
        }}
      >
        <Toolbar>
            <IconButton
              color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Vitrine de Projetos
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip 
              title={`Alternar para modo ${mode === 'light' ? 'escuro' : 'claro'}`} 
              TransitionComponent={Zoom}
            >
              <IconButton onClick={toggleColorMode} color="inherit" sx={{ mr: 2 }}>
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Meu Perfil" TransitionComponent={Zoom}>
              <Box 
                onClick={handleProfileClick}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  padding: '3px',
                  border: '2px solid transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    border: `2px solid ${theme.palette.primary.main}`,
                    transform: 'scale(1.08)'
                  },
                  '&:active': {
                    transform: 'scale(0.95)'
                  }
                }}
              >
                <Avatar 
                  src={avatarUrl}
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    transition: 'all 0.3s',
                    bgcolor: theme.palette.background.paper,
                    '&:hover': {
                      boxShadow: '0px 0px 8px rgba(0,0,0,0.2)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  {username ? username.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </Box>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Drawer lateral */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: open ? drawerWidth : drawerCollapsedWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : drawerCollapsedWidth,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            marginTop: '64px',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 64px)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        <DrawerHeader>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: '100%',
            py: 1
          }}>
            {open ? (
              <IconButton onClick={handleDrawerToggle}>
                <ChevronLeftIcon />
              </IconButton>
            ) : (
              <Tooltip title="Expandir menu" placement="right">
                <IconButton 
                  onClick={handleDrawerToggle} 
                  sx={{ 
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </DrawerHeader>
        
        <Divider />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          flex: '1 1 auto',
        }}>
          <List sx={{ mt: 1 }}>
            {getNavItems().map((item) => (
              <ListItem 
                key={item.text} 
                disablePadding 
                sx={{ 
                  display: 'block',
                  mb: 0.5
                }}
              >
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={isActive(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    mx: 1,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      backgroundColor: theme => 
                        theme.palette.mode === 'light' 
                          ? theme.palette.primary.light + '40'
                          : theme.palette.primary.dark + '40',
                      '&:hover': {
                        backgroundColor: theme => 
                          theme.palette.mode === 'light' 
                            ? theme.palette.primary.light + '60'
                            : theme.palette.primary.dark + '60',
                      }
                    }
                  }}
                >
                  <Tooltip title={!open ? item.text : ""} placement="right" arrow>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: isActive(item.path) ? 'primary.main' : 'inherit',
                        fontSize: !open ? 24 : 'default'
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  </Tooltip>
                  <ListItemText 
                    primary={item.text} 
                    sx={{ 
                      opacity: open ? 1 : 0,
                      display: open ? 'block' : 'none',
                      '& .MuiTypography-root': {
                        fontWeight: isActive(item.path) ? 600 : 400
                      }
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ mb: 1 }} />
          
          <List sx={{ height: 'auto', paddingBottom: 0 }}>
            <ListItem 
              disablePadding 
              sx={{ display: 'block', mb: 0 }}
            >
              <ListItemButton
                onClick={handleProfileClick}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  mx: 1,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    transform: 'translateX(8px)'
                  }
                }}
              >
                <Tooltip title={!open ? "Meu Perfil" : ""} placement="right" arrow>
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      fontSize: !open ? 24 : 'default'
                    }}
                  >
                    <ProfileIcon />
                  </ListItemIcon>
                </Tooltip>
                <ListItemText primary="Meu Perfil" sx={{ opacity: open ? 1 : 0, display: open ? 'block' : 'none' }} />
              </ListItemButton>
            </ListItem>
            
            {/* Separador antes do botão de sair */}
            <Divider sx={{ my: 0.25, mx: 2 }} />
            
            <ListItem 
              disablePadding 
              sx={{ display: 'block', mt: 0 }}
              onClick={handleLogout}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  mx: 1,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.error.main}`,
                  '&:hover': {
                    backgroundColor: theme.palette.error.light,
                    transform: 'translateX(8px)'
                  }
                }}
              >
                <Tooltip title={!open ? "Sair" : ""} placement="right" arrow>
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: theme.palette.error.main,
                      fontSize: !open ? 24 : 'default'
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                </Tooltip>
                <ListItemText 
                  primary="Sair" 
                  sx={{ 
                    opacity: open ? 1 : 0,
                    display: open ? 'block' : 'none',
                    color: theme.palette.error.main,
                    fontWeight: 'bold'
                  }} 
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      {/* Conteúdo principal */}
      <Main open={open}>
        {children}
      </Main>
    </Box>
  );
};

export default Navbar; 